import { useEffect } from 'react';
import { useForm } from '@inertiajs/react';

const taskTypes = {
    call: 'Call',
    meeting: 'Meeting',
    task: 'Task',
    email: 'Email',
    reminder: 'Reminder',
};

const priorities = {
    low: 'Low',
    medium: 'Medium',
    high: 'High',
};

const statuses = {
    pending: 'Pending',
    in_progress: 'In Progress',
    completed: 'Completed',
    cancelled: 'Cancelled',
};

export default function TaskForm({
    queryId,
    task = null,
    users = [],
    onClose,
    onSuccess,
}) {
    const isEditing = !!task;

    const { data, setData, post, put, processing, errors, reset } = useForm({
        type: task?.type || 'task',
        subject: task?.subject || '',
        description: task?.description || '',
        assigned_to: task?.assigned_to || '',
        due_date: task?.due_date ? task.due_date.substring(0, 10) : '',
        due_time: task?.due_time ? task.due_time.substring(0, 5) : '',
        reminder_at: task?.reminder_at ? task.reminder_at.substring(0, 16) : '',
        priority: task?.priority || 'medium',
        status: task?.status || 'pending',
        outcome: task?.outcome || '',
        is_dsr: task?.is_dsr || false,
    });

    useEffect(() => {
        if (task) {
            setData({
                type: task.type || 'task',
                subject: task.subject || '',
                description: task.description || '',
                assigned_to: task.assigned_to || '',
                due_date: task.due_date ? task.due_date.substring(0, 10) : '',
                due_time: task.due_time ? task.due_time.substring(0, 5) : '',
                reminder_at: task.reminder_at ? task.reminder_at.substring(0, 16) : '',
                priority: task.priority || 'medium',
                status: task.status || 'pending',
                outcome: task.outcome || '',
                is_dsr: task.is_dsr || false,
            });
        }
    }, [task]);

    const handleSubmit = (e) => {
        e.preventDefault();

        const options = {
            preserveScroll: true,
            onSuccess: () => {
                reset();
                onSuccess?.();
                onClose?.();
            },
        };

        if (isEditing) {
            put(`/queries/${queryId}/tasks/${task.id}`, options);
        } else {
            post(`/queries/${queryId}/tasks`, options);
        }
    };

    return (
        <div className="modal-backdrop show" onClick={onClose}>
            <div className="modal-dialog modal-lg" onClick={(e) => e.stopPropagation()}>
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">
                            <i className={`bi ${isEditing ? 'bi-pencil' : 'bi-plus-lg'} me-2`}></i>
                            {isEditing ? 'Edit Task' : 'Create Task'}
                        </h5>
                        <button type="button" className="btn-close" onClick={onClose}></button>
                    </div>

                    <form onSubmit={handleSubmit}>
                        <div className="modal-body">
                            <div className="row g-3">
                                <div className="col-md-4">
                                    <label className="form-label">Task Type <span className="text-danger">*</span></label>
                                    <select
                                        className={`form-select ${errors.type ? 'is-invalid' : ''}`}
                                        value={data.type}
                                        onChange={(e) => setData('type', e.target.value)}
                                    >
                                        {Object.entries(taskTypes).map(([key, label]) => (
                                            <option key={key} value={key}>{label}</option>
                                        ))}
                                    </select>
                                    {errors.type && <div className="invalid-feedback">{errors.type}</div>}
                                </div>

                                <div className="col-md-4">
                                    <label className="form-label">Priority <span className="text-danger">*</span></label>
                                    <select
                                        className={`form-select ${errors.priority ? 'is-invalid' : ''}`}
                                        value={data.priority}
                                        onChange={(e) => setData('priority', e.target.value)}
                                    >
                                        {Object.entries(priorities).map(([key, label]) => (
                                            <option key={key} value={key}>{label}</option>
                                        ))}
                                    </select>
                                    {errors.priority && <div className="invalid-feedback">{errors.priority}</div>}
                                </div>

                                {isEditing && (
                                    <div className="col-md-4">
                                        <label className="form-label">Status</label>
                                        <select
                                            className={`form-select ${errors.status ? 'is-invalid' : ''}`}
                                            value={data.status}
                                            onChange={(e) => setData('status', e.target.value)}
                                        >
                                            {Object.entries(statuses).map(([key, label]) => (
                                                <option key={key} value={key}>{label}</option>
                                            ))}
                                        </select>
                                        {errors.status && <div className="invalid-feedback">{errors.status}</div>}
                                    </div>
                                )}

                                <div className="col-12">
                                    <label className="form-label">Subject <span className="text-danger">*</span></label>
                                    <input
                                        type="text"
                                        className={`form-control ${errors.subject ? 'is-invalid' : ''}`}
                                        value={data.subject}
                                        onChange={(e) => setData('subject', e.target.value)}
                                        placeholder="Enter task subject..."
                                    />
                                    {errors.subject && <div className="invalid-feedback">{errors.subject}</div>}
                                </div>

                                <div className="col-12">
                                    <label className="form-label">Description</label>
                                    <textarea
                                        className="form-control"
                                        rows="3"
                                        value={data.description}
                                        onChange={(e) => setData('description', e.target.value)}
                                        placeholder="Task details..."
                                    ></textarea>
                                </div>

                                <div className="col-md-6">
                                    <label className="form-label">Assigned To</label>
                                    <select
                                        className="form-select"
                                        value={data.assigned_to}
                                        onChange={(e) => setData('assigned_to', e.target.value)}
                                    >
                                        <option value="">Not assigned</option>
                                        {users.map((user) => (
                                            <option key={user.id} value={user.id}>{user.name}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="col-md-3">
                                    <label className="form-label">Due Date</label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={data.due_date}
                                        onChange={(e) => setData('due_date', e.target.value)}
                                    />
                                </div>

                                <div className="col-md-3">
                                    <label className="form-label">Due Time</label>
                                    <input
                                        type="time"
                                        className="form-control"
                                        value={data.due_time}
                                        onChange={(e) => setData('due_time', e.target.value)}
                                    />
                                </div>

                                <div className="col-md-6">
                                    <label className="form-label">Reminder</label>
                                    <input
                                        type="datetime-local"
                                        className="form-control"
                                        value={data.reminder_at}
                                        onChange={(e) => setData('reminder_at', e.target.value)}
                                    />
                                </div>

                                <div className="col-md-6 d-flex align-items-end">
                                    <div className="form-check">
                                        <input
                                            type="checkbox"
                                            className="form-check-input"
                                            id="is_dsr"
                                            checked={data.is_dsr}
                                            onChange={(e) => setData('is_dsr', e.target.checked)}
                                        />
                                        <label className="form-check-label" htmlFor="is_dsr">
                                            Include in Daily Status Report (DSR)
                                        </label>
                                    </div>
                                </div>

                                {isEditing && data.status === 'completed' && (
                                    <div className="col-12">
                                        <label className="form-label">Outcome</label>
                                        <textarea
                                            className="form-control"
                                            rows="2"
                                            value={data.outcome}
                                            onChange={(e) => setData('outcome', e.target.value)}
                                            placeholder="Task outcome or result..."
                                        ></textarea>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="modal-footer">
                            <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
                                Cancel
                            </button>
                            <button type="submit" className="btn btn-primary" disabled={processing}>
                                {processing ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2"></span>
                                        {isEditing ? 'Saving...' : 'Creating...'}
                                    </>
                                ) : (
                                    <>
                                        <i className={`bi ${isEditing ? 'bi-check-lg' : 'bi-plus-lg'} me-1`}></i>
                                        {isEditing ? 'Save Changes' : 'Create Task'}
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            <style>{`
                .modal-backdrop {
                    position: fixed;
                    inset: 0;
                    background: rgba(0, 0, 0, 0.5);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 1050;
                    padding: 1rem;
                }

                .modal-dialog {
                    width: 100%;
                    max-width: 700px;
                    margin: 0;
                }

                .modal-content {
                    border-radius: 12px;
                    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
                    border: none;
                }

                .modal-header {
                    border-bottom: 1px solid #e2e8f0;
                    padding: 1rem 1.5rem;
                }

                .modal-title {
                    font-weight: 600;
                    color: #1e293b;
                }

                .modal-body {
                    padding: 1.5rem;
                }

                .modal-footer {
                    border-top: 1px solid #e2e8f0;
                    padding: 1rem 1.5rem;
                }

                .form-label {
                    font-weight: 500;
                    font-size: 0.875rem;
                    color: #475569;
                    margin-bottom: 0.375rem;
                }
            `}</style>
        </div>
    );
}
